import { HttpContextToken, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

const RETRY_ONCE_CONTEXT = new HttpContextToken<boolean>(() => false);

function normalizeBase(url: string | undefined): string {
  return (url || '').replace(/\/+$/, '');
}

function configuredApiBases(): string[] {
  return Array.from(
    new Set(
      [environment.API_BASE_URL, environment.AUTH_API_BASE_URL]
        .map(normalizeBase)
        .filter((value) => !!value),
    ),
  );
}

function isApiRequest(url: string): boolean {
  if (url.startsWith('/api/')) return true;
  return configuredApiBases().some((base) => url.startsWith(`${base}/api/`));
}

function isAuthBypassRequest(url: string): boolean {
  const bypassPaths = [
    '/api/auth/login',
    '/api/auth/refresh',
    '/api/auth/forgot',
    '/api/auth/confirm-forgot',
    '/api/auth/complete-new-password',
    '/api/auth/logout',
  ];

  if (bypassPaths.some(path => url.startsWith(path))) return true;
  return configuredApiBases().some((base) =>
    bypassPaths.some((path) => url.startsWith(`${base}${path}`)),
  );
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  if (!isApiRequest(req.url) || isAuthBypassRequest(req.url)) {
    return next(req);
  }

  const auth = inject(AuthService);
  const requestedToken = req.headers.get('Authorization')?.replace(/^Bearer\s+/i, '') || null;
  const storedTokens = auth.tokens();
  const idPreferred = req.url.includes('/api/auth/me') || requestedToken === storedTokens?.idToken;

  return from(auth.getValidToken(idPreferred)).pipe(
    switchMap((token) => {
      const authReq = token
        ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
        : req.headers.has('Authorization')
          ? req.clone({ headers: req.headers.delete('Authorization') })
          : req;

      return next(authReq).pipe(
        catchError((error) => {
          const code = error?.status;
          const alreadyRetried = authReq.context.get(RETRY_ONCE_CONTEXT);
          const shouldRetry = (code === 401 || code === 403) && !alreadyRetried;

          if (!shouldRetry) {
            return throwError(() => error);
          }

          return from(auth.refreshNowSingleFlight(true)).pipe(
            switchMap((refreshed) => {
              if (!refreshed) {
                return throwError(() => error);
              }

              const retryToken = idPreferred ? refreshed.idToken : refreshed.accessToken;
              if (!retryToken) {
                return throwError(() => error);
              }

              const retryReq = req.clone({
                setHeaders: { Authorization: `Bearer ${retryToken}` },
                context: req.context.set(RETRY_ONCE_CONTEXT, true),
              });

              return next(retryReq);
            }),
            catchError(() => throwError(() => error)),
          );
        }),
      );
    }),
  );
};
